import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { buildInitialWhereFromFilters, FilterBar } from '@client/components/filters';
import {
    type ObligationReassignmentAction,
    ObligationReassignmentDrawer,
} from '@client/components/obligations/ObligationReassignmentDrawer';
import { ObligationStatusUpdateModal } from '@client/components/obligations/ObligationStatusUpdateModal';
import { useObligationFilterConfig } from '@client/components/obligations/useObligationFilterConfig';
import {
    type BuiltInTableView,
    builtInViewPayload,
    TableViewsBar,
    useTableViews,
    type ViewParamsSpec,
} from '@client/components/table-views';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { selectIsLiteOrg, selectOrg, selectUser, useAuthStore } from '@client/stores/AuthStore';
import { queryClient, type RouterInputs, trpcOptions } from '@client/trpc/client';
import {
    DETAIL_LOOKUP_PREFETCH_OPTIONS,
    LIST_ROUTE_NAVIGATION_PREFETCH_OPTIONS,
    LIST_ROUTE_PREFETCH_OPTIONS,
    LIST_ROUTE_QUERY_OPTIONS,
} from '@client/trpc/query-options';
import type { ShareableRouteContext } from '@client/types/router';
import { TokenRefreshManager } from '@client/utils/auth/TokenRefreshManager';
import {
    DEFAULT_MAX_ACCESSIBLE_RECORDS,
    DEFAULT_PAGE_SIZE,
    OBLIGATION_STATUSES_KEYS,
    ORG_ROLES,
} from '@shared/constants';
import { FilterOperatorSchema } from '@shared/schemas/filter';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import {
    runDetailRoutePrefetchWhenAuthorised,
    shouldPrefetchDetailRouteData,
    yieldToPrefetchBatch,
} from '../../detail-route-prefetch';
import {
    freshObligationsListInput,
    freshObligationsListInputWithWhere,
    LIST_TABLE_PERSIST_KEYS,
    OBLIGATION_VIEW_PARAM_DEFAULTS,
    OBLIGATIONS_DEFAULT_ORDER_BY,
} from '../../fresh-list-inputs';
import { readStoredQueryParams, runListRoutePrefetch, savedViewsQueryCache } from '../../list-route-prefetch';
import { useObligationColumns } from './columns';

/**
 * Three fixed views for the obligations register (CUR-386), served by the saved-views engine. The
 * latest/all split is a non-filter query param (`latestPerFamily`, a special server-side behaviour —
 * see `buildObligationListWhere`), declared via the params spec so views can carry it.
 *  - Latest version (default): the engine's built-in "All" tab, relabelled — the *neutral* view is
 *    the current version of every obligation family, so the params spec defaults carry
 *    `latestPerFamily: true`. Making it the "All" tab also puts it first and makes it the fresh-load
 *    default, matching the ticket's required order without a starred saved view.
 *  - All versions: built-in preset switching `latestPerFamily` off (every version of every family).
 *  - Awaiting review: built-in preset — an ordinary status filter, all versions.
 * User-saved views sit on top of these; the built-ins always render.
 */
const OBLIGATION_VIEW_PARAMS: ViewParamsSpec = {
    schema: z.object({ latestPerFamily: z.boolean() }),
    // Shared with the route's `beforeLoad` prefetch input, so a change here moves both keys.
    defaults: OBLIGATION_VIEW_PARAM_DEFAULTS,
};

const OBLIGATION_BUILT_IN_VIEWS: BuiltInTableView[] = [
    {
        id: '__all-versions__',
        name: 'All versions',
        description: 'Every version of every obligation',
        icon: 'folder',
        emptyTitle: 'No obligations yet',
        payload: builtInViewPayload([], { latestPerFamily: false }),
    },
    {
        id: '__awaiting-review__',
        name: 'Awaiting review',
        description: 'Obligation versions awaiting review',
        icon: 'clock',
        emptyTitle: 'No obligations awaiting review',
        emptyDescription: 'Obligation versions sent for review appear here.',
        payload: builtInViewPayload(
            [
                {
                    id: 'awaiting-review-status',
                    filterId: 'status',
                    operator: 'in',
                    value: [
                        OBLIGATION_STATUSES_KEYS.AWAITING_REVIEW,
                        OBLIGATION_STATUSES_KEYS.NEW_VERSION_AWAITING_REVIEW,
                    ],
                },
            ],
            { latestPerFamily: false },
        ),
    },
];

const ObligationSearchSchema = z.object({
    'obligation-filters': z
        .array(
            z.union([
                z.object({
                    id: z.string(),
                    filterId: z.literal('owner'),
                    operator: FilterOperatorSchema,
                    value: z.union([z.array(z.coerce.bigint()), z.coerce.bigint()]),
                }),
                z.object({
                    id: z.string(),
                    filterId: z.string(),
                    operator: FilterOperatorSchema,
                    value: z.any(),
                }),
            ]),
        )
        .optional()
        .catch(undefined),
    /** Saved-view deep link (same contract as claims/incidents). */
    view: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/console/$orgId/compliance/obligations/')({
    staticData: {
        ui: {
            title: 'Obligations',
            subtitle: 'Create, track, and manage obligations',
        },
    },
    validateSearch: zodValidator(ObligationSearchSchema),
    beforeLoad: ({
        params,
        search,
        location,
        preload,
        abortController,
        cause,
    }): ShareableRouteContext | Promise<ShareableRouteContext> => {
        // The path state as it stood at navigation: if the prefetch below waits for a token refresh,
        // the page can mount first and store its initial input, which is not restored state.
        const storedQueryParams = readStoredQueryParams<RouterInputs['obligation']['listObligations']>(
            location.pathname,
        );

        // Start the saved-views round trip and (when the input is exactly computable) the list
        // query itself from the router, as the claims and incidents lists do, so the batch overlaps
        // the page mount instead of waiting for it. The filter bar's lookups ride the same batch.
        runDetailRoutePrefetchWhenAuthorised(
            () => {
                const authState = useAuthStore.getState();
                const user = selectUser(authState);
                // Obligations are hidden for Curium Lite (`LITE_HIDDEN_ROUTE_PREFIXES`): the page is Not found.
                if (selectIsLiteOrg(authState)) {
                    return false;
                }
                return shouldPrefetchDetailRouteData({
                    hasValidToken: authState.hasValidToken({
                        minValidityMs: TokenRefreshManager.REFRESH_BUFFER_MS,
                    }),
                    userRoles: user?.roles,
                    allowedRoles: [ORG_ROLES.USER, ORG_ROLES.SHARED_EDIT_USER, ORG_ROLES.ORG_ADMIN],
                    hydratedOrg: selectOrg(authState),
                    routeOrgId: params.orgId,
                    requiredModule: 'compliance',
                });
            },
            () => {
                runListRoutePrefetch<RouterInputs['obligation']['listObligations'], 'OBLIGATION'>({
                    savedViewsUtils: savedViewsQueryCache(queryClient, trpcOptions.savedView.listSavedViews),
                    entityType: 'OBLIGATION',
                    pathname: location.pathname,
                    storedQueryParams,
                    urlFilterCount: search['obligation-filters']?.length ?? 0,
                    urlViewId: search.view,
                    defaultInput: freshObligationsListInput(),
                    prefetchList: (input) => {
                        // The page applies a filter deep link after mount and drops it from the URL; that
                        // `'stay'` run would see no URL filters and warm the stale pre-filter stored input.
                        // The mounted query owns every same-route change.
                        if (cause === 'stay') {
                            return;
                        }
                        void queryClient.prefetchQuery(
                            trpcOptions.obligation.listObligations.queryOptions(
                                input,
                                cause === 'enter'
                                    ? LIST_ROUTE_NAVIGATION_PREFETCH_OPTIONS
                                    : LIST_ROUTE_PREFETCH_OPTIONS,
                            ),
                        );
                    },
                });
                // The filter bar's options (`useObligationFilterConfig`) would otherwise leave as a second
                // batch after mount. Not on a hover preload: the 5 s window would resend them on each hover,
                // and the `'enter'` run still sends them before the page mounts.
                if (preload) {
                    return;
                }
                void queryClient.prefetchQuery(
                    trpcOptions.handlingParty.listHandlingParties.queryOptions({}, DETAIL_LOOKUP_PREFETCH_OPTIONS),
                );
                void queryClient.prefetchQuery(
                    trpcOptions.obligation.getObligationColumnsForFiltering.queryOptions(
                        undefined,
                        DETAIL_LOOKUP_PREFETCH_OPTIONS,
                    ),
                );
            },
            { defer: !preload, signal: abortController.signal },
        );
        return cause === 'enter' ? yieldToPrefetchBatch() : undefined;
    },
    component: ObligationSearch,
});

function ObligationSearch() {
    const orgId = useOrgId();
    const { trpc } = useTrpc();
    const navigate = useNavigate();
    const searchParams = Route.useSearch();
    const [search, setSearch] = useState(['']);
    const [statusUpdateModalVisible, setStatusUpdateModalVisible] = useState(false);
    const [reassignmentAction, setReassignmentAction] = useState<ObligationReassignmentAction>(['none']);

    const { config: filterConfig, ready: filterConfigReady } = useObligationFilterConfig();

    // NOTE: the path-state key must be 'query-params' — useTableViews' return-visit detection
    // (hasStoredPathState) looks for exactly that key.
    // The default matches the params-spec defaults (the "Latest version" tab): one row per obligation
    // family, and the route's `beforeLoad` prefetches the same input. A filter deep link seeds its
    // `where` so the stored input matches the filtered query (as on incidents). The shared
    // ObligationsTableDrawer used by Controls + Incidents does NOT opt in, so its callers see all
    // versions as before.
    const [queryParams, setQueryParams] = usePathState<RouterInputs['obligation']['listObligations']>(
        'query-params',
        freshObligationsListInputWithWhere(
            buildInitialWhereFromFilters(filterConfig, searchParams['obligation-filters']),
        ),
    );

    // Postpone the list query until saved views resolve which view loads first, so we fetch once
    // with the resolved where/params/sort instead of unfiltered-then-refiltered (set by onResolved).
    const [viewsReady, setViewsReady] = useState(false);

    const { data, isPending, refetch } = trpc.obligation.listObligations.useQuery(queryParams, {
        enabled: viewsReady,
        ...LIST_ROUTE_QUERY_OPTIONS,
    });

    const columns = useObligationColumns({
        search,
    });

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: !viewsReady || isPending,
        columns,
        rowKey: 'id',
        queryParams: queryParams,
        onParamsChange: setQueryParams,
        emptyState: { noRecordsTitle: 'No obligations yet' },
        pagination: { position: 'both', maxAccessibleRecords: DEFAULT_MAX_ACCESSIBLE_RECORDS },
        columnVisibility: {
            enabled: true,
            persistKey: LIST_TABLE_PERSIST_KEYS.obligations,
            enableReordering: true,
            defaultHidden: ['section'],
        },
        refreshable: { enabled: true, onRefresh: () => refetch() },
        rowSelection: {
            enabled: true,
            type: 'checkbox',
            preserveSelectedRowKeys: true,
        },
    });

    const views = useTableViews({
        entityType: 'OBLIGATION',
        config: filterConfig,
        configReady: filterConfigReady,
        urlFilterKey: 'obligation-filters',
        builtInViews: OBLIGATION_BUILT_IN_VIEWS,
        builtinAllLabel: 'Latest version',
        viewParams: OBLIGATION_VIEW_PARAMS,
        initialFilters: searchParams['obligation-filters'],
        initialActiveViewId: searchParams.view,
        queryParams,
        setQueryParams,
        table: tableProps,
        columnPersistKey: LIST_TABLE_PERSIST_KEYS.obligations,
        defaultOrderBy: OBLIGATIONS_DEFAULT_ORDER_BY,
        defaultPageSize: DEFAULT_PAGE_SIZE,
        defaultHidden: ['section'],
        onResolved: () => setViewsReady(true),
    });

    // Search-term highlight for the description/code columns. Derived from the engine's filter
    // state post-render (the columns feed usePrismaTable, which the engine needs — so the terms
    // can't be read before the hook runs). Same one-render lag as the old onChange wiring.
    // Content-equal terms bail out (return prev) — searchTerms' identity is not stable across
    // renders (its memo keys on the filter config, which rebuilds with its query data), and an
    // unconditional set would re-render in a loop.
    const searchTerms = views.filterState.searchTerms;
    useEffect(() => {
        const next = searchTerms && searchTerms.length > 0 ? searchTerms : [''];
        setSearch((prev) => (prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next));
    }, [searchTerms]);

    return (
        <PageController route={Route}>
            <TableViewsBar controller={views.controller} />

            <TableToolbar
                filters={
                    <FilterBar
                        config={filterConfig}
                        filters={views.filterState.filters}
                        whereClause={views.filterState.whereClause}
                        onAddFilter={views.filterState.addFilter}
                        onUpdateFilter={views.filterState.updateFilter}
                        onRemoveFilter={views.filterState.removeFilter}
                        onClearFilters={views.filterState.clearFilters}
                    />
                }
                actions={
                    <>
                        <Button.Selection
                            type="secondary"
                            selectedCount={tableProps.selectedRowKeys.length}
                            action="Reassign"
                            noun="obligations"
                            nounSingular="obligation"
                            onClick={() =>
                                setReassignmentAction([
                                    'selected',
                                    (tableProps.selectedRowKeys as (string | number)[]).map((k) => BigInt(String(k))),
                                ])
                            }
                        />
                        <Button.Selection
                            type="secondary"
                            selectedCount={tableProps.selectedRowKeys.length}
                            action="Update status for"
                            noun="obligations"
                            nounSingular="obligation"
                            selectionHint="Select one or more obligations to update their status"
                            onClick={() => setStatusUpdateModalVisible(true)}
                        />
                        <Button.Add.Link
                            to="/console/$orgId/compliance/obligations/add"
                            params={{ orgId }}
                            variant="primary"
                            label="New obligation"
                        />
                    </>
                }
            />

            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/compliance/obligations/details/$id/manage',
                                params: {
                                    orgId,
                                    id,
                                },
                            },
                            e,
                        );
                    },
                })}
            />

            <ObligationStatusUpdateModal
                open={statusUpdateModalVisible}
                selectedObligationIds={tableProps.selectedRowKeys as bigint[]}
                onClose={() => setStatusUpdateModalVisible(false)}
                onSuccess={() => {
                    tableProps.clearSelection();
                    refetch();
                }}
            />

            <ObligationReassignmentDrawer
                action={reassignmentAction}
                onClose={(resetSelectedRowKeys) => {
                    setReassignmentAction(['none']);

                    if (resetSelectedRowKeys) {
                        tableProps.clearSelection();
                    }
                }}
            />
        </PageController>
    );
}
